import React, { useEffect, useState, useRef } from "react";
import { Calendar, View } from "react-big-calendar";
import withDragAndDrop, { withDragAndDropProps } from "react-big-calendar/lib/addons/dragAndDrop";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";
import { localizer } from "./component/calendar-utils";
import "./component/calendar.css";
import BaseService from "../../service/base.service";
import Appointments from "../../models/appointment";
import BasePatientInfo from "../../models/basePatient";
import notify from "../../components/common/notificationHelper";
import { registerPushNotifications } from "../../utils/pushRegistration";
import { useIsMobile, MOBILE_BREAKPOINT } from "../../hooks/useIsMobile";
import {
  Spin,
  Button,
  Modal,
  Form,
  Alert,
  DatePicker,
  Select,
  SelectProps,
  Tooltip, Input
} from "antd";
import { EditOutlined, CalendarOutlined, CloseOutlined, SaveOutlined, DeleteOutlined } from '@ant-design/icons';
import { format } from "date-fns";
import { Dayjs } from "dayjs";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
dayjs.extend(isoWeek);

interface FormValues {
  id: number;
  startTime: Dayjs | null;
  endTime: Dayjs | null;
  reason: string;
  notes: string;
  reminderTime: number;
  patientId: number;
}

const AppointmentCalendar: React.FC = () => {
  const isMobile = useIsMobile();
  const [appointments, setAppointments] = useState<Appointments[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [deleteMode, setDeleteMode] = useState(false);
  const [form] = Form.useForm<FormValues>();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentView, setCurrentView] = useState<View>(() =>
    typeof window !== "undefined" && window.innerWidth < MOBILE_BREAKPOINT ? "day" : "week"
  );
  const [options, setOptions] = useState<SelectProps["options"]>([]);
  const [fetching, setFetching] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<Appointments | null>(null);
  const [loading, setLoading] = useState(false);
  const horizontalPadding = isMobile ? 12 : 24;
  const contentRef = useRef<HTMLDivElement | null>(null);

  const DnDCalendar = withDragAndDrop<Appointments, object>(Calendar);

  const [pendingDrag, setPendingDrag] = useState<{
    event: Appointments;
    start: Date;
    end: Date;
  } | null>(null);


  useEffect(() => {
    fetchAppointmentsFor(currentDate, currentView);
  }, []);

  useEffect(() => {
    if (isMobile && currentView === "week") {
      setCurrentView("day");
      fetchAppointmentsFor(currentDate, "day");
    }
  }, [isMobile]);

  useEffect(() => {
    if (isModalOpen) {
      requestAnimationFrame(() => {
        setTimeout(() => {
          if (contentRef.current) {
            contentRef.current.scrollTop = 0;
          } else {
            const modalBody = document.querySelector(".ant-modal-body") as HTMLElement | null;
            if (modalBody) modalBody.scrollTop = 0;
          }
        }, 0);
      });
    }
  }, [isModalOpen]);

  useEffect(() => {
    registerPushNotifications();
  }, []);

  const fetchAppointmentsFor = async (date: Date, view: View) => {
    setLoading(true);
    const { start, end } = getViewRange(date, view);
    const startStr = dayjs(start).format("DD.MM.YYYY HH:mm");
    const endStr = dayjs(end).format("DD.MM.YYYY HH:mm");
    try {
      const url = `/appointments?start=${startStr}&end=${endStr}`;
      const rp = await BaseService.getAll<Appointments>(url);
      if (rp.Status) {
        const data = rp.Data || [];
        setAppointments(
          data.map(
            (a: any) =>
              new Appointments(
                a.id,
                a.title,
                dayjs(a.start, "DD.MM.YYYY HH:mm").toDate(),
                dayjs(a.end, "DD.MM.YYYY HH:mm").toDate(),
                a.reason,
                a.notes,
                a.reminderTime,
                a.patientId,
                a.patientName
              )
          )
        );
      }
    } catch (err) {
      console.error("Error fetching appointments:", err);
    }
    finally {
      setLoading(false);
    }
  };

  const handleAddAndEditAppointment = async (values: FormValues) => {
    const formattedValues = {
      ...values,
      startTime: values.startTime?.format("DD.MM.YYYY HH:mm"),
      endTime: values.endTime?.format("DD.MM.YYYY HH:mm"),
      reminderTime: values.reminderTime ?? null
    };
    const selectedPatient = options?.find((opt) => opt.value === values.patientId);
    const patientName = selectedPatient?.label || 'the patient';
    const time = values.startTime?.format('MMMM D [at] HH:mm');
    if (isEdit) {
      await BaseService.update("/appointments/", values.id, formattedValues).then((rp) => {
        if (rp.Status) {
          notify({
            title: 'Appointment Updated',
            description: `The appointment with ${patientName} \n has been successfully updated for ${time}.`,
            variant: 'success',
          });
          fetchAppointmentsFor(currentDate, currentView);
        } else {
          notify({
            title: 'Update Failed',
            description: `Could not update the appointment with ${patientName}. Please try again.`,
            variant: 'error',
          });
          console.log("Messages: " + rp.Messages);
          console.log("Exception: " + rp.Exception);
        }
      });
    } else {
      await BaseService.create(`/appointments/${values.patientId}`, formattedValues).then((rp) => {
        if (rp.Status) {
          notify({
            title: 'Appointment Scheduled',
            description: `Appointment with ${patientName} has been successfully scheduled for ${time}.`,
            variant: 'success',
          });
          fetchAppointmentsFor(currentDate, currentView);
        } else {
          notify({
            title: 'Scheduling Failed',
            description: `Could not schedule the appointment with ${patientName}. Please try again.`,
            variant: 'error',
          });
          console.log('Messages:', rp.Messages);
          console.log('Exception:', rp.Exception);
        }
      });
    }
    setIsModalOpen(false);
    fetchAppointmentsFor(currentDate, currentView);
  };

  const handleConfirmDelete = async () => {
    if (!selectedEvent) return;
    const url = `/appointments/${selectedEvent.id}/patients/${selectedEvent.patientId}`;
    await BaseService.delete(url, null).then((rp) => {
      const patientId = selectedEvent.patientId;
      const selectedPatient = options?.find((opt) => opt.value === patientId);
      const patientName = selectedPatient?.label || 'the patient';
      if (rp.Status) {
        notify({
          title: 'Appointment Deleted',
          description: `The appointment with ${patientName} has been successfully deleted.`,
          variant: 'info',
        });
        fetchAppointmentsFor(currentDate, currentView);
      } else {
        notify({
          title: 'Delete Failed',
          description: `Could not delete the appointment with ${patientName}. Please try again.`,
          variant: 'error',
        });
      }
    });
    setDeleteMode(false);
    setIsModalOpen(false);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setDeleteMode(false);
    setOptions([]);
  };

  const handleAdd = () => {
    setIsEdit(false);
    form.resetFields();
    setIsModalOpen(true);
    setDeleteMode(false);
    setOptions([]);
  };

  const getViewRange = (date: Date, view: View) => {
    const day = dayjs(date);
    let start: dayjs.Dayjs;
    let end: dayjs.Dayjs;

    switch (view) {
      case "month":
        start = day.startOf("month");
        end = day.endOf("month");
        break;
      case "week":
        start = day.startOf("isoWeek"); // Monday start
        end = day.endOf("isoWeek"); // Sunday end
        break;
      case "day":
        start = day.startOf("day");
        end = day.endOf("day");
        break;
      default:
        start = day.startOf("isoWeek");
        end = day.endOf("isoWeek");
    }

    return { start: start.toDate(), end: end.toDate() };
  };

  const handleSelectEvent = (event: Appointments) => {
    setSelectedEvent(event);
    setIsEdit(true);
    form.setFieldsValue({
      id: event.id,
      startTime: dayjs(event.start),
      endTime: dayjs(event.end),
      reason: event.reason,
      notes: event.notes,
      patientId: event.patientId,
      reminderTime: event.reminderTime ?? null
    });
    setOptions([{ label: event.patientName, value: event.patientId }]);
    setIsModalOpen(true);
    setDeleteMode(false);
  };

  const handleSearch = async (query: string) => {
    if (!query) return;
    setFetching(true);
    try {
      const rp = await BaseService.getAll<BasePatientInfo>(`/patients/search?searchTerm=${query}`);
      if (rp.Status) {
        setOptions(
          rp.Data.map((p: BasePatientInfo) => ({ label: p.fullName, value: p.id }))
        );
      }
    } finally {
      setFetching(false);
    }
  };

  const handleEventDrop = ({ event, start, end }: any) => {
    setPendingDrag({ event, start, end });
  };

  const handleEventResize = async ({ event, start, end }: any) => {
    try {
      const updatedEvent = { ...event, start, end };
      await BaseService.update(`/appointments/`, event.id, {
        startTime: dayjs(start).format("DD.MM.YYYY HH:mm"),
        endTime: dayjs(end).format("DD.MM.YYYY HH:mm"),
      });

      notify({
        title: "Appointment Resized",
        description: `${event.patientName}'s appointment duration has been changed.`,
        variant: "info",
      });

      setAppointments((prev) =>
        prev.map((a) => (a.id === event.id ? updatedEvent : a))
      );
    } catch (error) {
      notify({
        title: "Error",
        description: "Could not resize the appointment.",
        variant: "error",
      });
    }
  };


  const CustomEvent = ({ event }: { event: any }) => (
    <Tooltip title="Click to edit appointment">
      <div className="custom-event" style={{ fontSize: isMobile ? 11 : undefined, lineHeight: 1.2 }}>
        <strong>{event.title}</strong>
        {!isMobile && <div>{event.patientName}</div>}
      </div>
    </Tooltip>
  );

  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        height: "100%",
        padding: isMobile ? "8px 0 16px" : "16px 16px 24px",
        overflow: "hidden",
        boxSizing: "border-box",
      }}
    >
      <div style={{ marginBottom: isMobile ? 12 : 20, flexShrink: 0 }}>
        <Alert
          message={
            isMobile
              ? "View and manage patient appointments."
              : "View, schedule, and manage patient appointments using the calendar below."
          }
          type="info"
          showIcon={!isMobile}
          style={{
            background: "#f0f7ff",
            borderColor: "#d0e4ff",
            color: "#2c5282",
            borderRadius: 8,
            padding: isMobile ? "8px 12px" : "10px 14px",
            fontSize: isMobile ? 13 : 14,
          }}
        />
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: isMobile ? "stretch" : "flex-end",
          marginBottom: isMobile ? 12 : 16,
          flexShrink: 0,
        }}
      >
        <Button
          type="primary"
          onClick={handleAdd}
          icon={<CalendarOutlined />}
          block={isMobile}
          style={{
            borderRadius: 6,
            padding: "6px 18px",
            fontWeight: 500,
          }}
        >
          Add Appointment
        </Button>
      </div>
      <div
        className="appointments-calendar"
        style={{
          flex: 1,
          minHeight: 0,
          background: "#fff",
          borderRadius: isMobile ? 12 : 16,
          boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
          padding: isMobile ? 8 : 16,
          position: "relative",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <DnDCalendar
          localizer={localizer}
          events={appointments}
          view={currentView}
          date={currentDate}
          draggableAccessor={() => !isMobile}
          resizable={!isMobile}
          onEventDrop={isMobile ? undefined : handleEventDrop}
          onEventResize={isMobile ? undefined : handleEventResize}
          popup
          selectable
          onView={(view) => {
            setCurrentView(view);
            fetchAppointmentsFor(currentDate, view);
          }}
          onNavigate={(date) => {
            setCurrentDate(date);
            fetchAppointmentsFor(date, currentView);
          }}
          startAccessor={(event) => event.start}
          endAccessor={(event) => event.end}
          onSelectEvent={handleSelectEvent}
          onSelectSlot={(slotInfo) => {
            setIsEdit(false);
            form.resetFields();
            setIsModalOpen(true);
            setOptions([]);
            form.setFieldsValue({
              startTime: dayjs(slotInfo.start),
              endTime: dayjs(slotInfo.end),
            });
          }}
          views={isMobile ? ["month", "day", "agenda"] : ["month", "week", "day", "agenda"]}
          defaultView={isMobile ? "day" : "week"}
          components={{ event: CustomEvent }}
          style={{
            flex: 1,
            width: "100%",
            minHeight: 0,
            backgroundColor: "#fff",
            borderRadius: 12,
          }}
          eventPropGetter={() => ({
            style: {
              backgroundColor: "#eaf2ff",
              color: "#1976d2",
              cursor: isMobile ? "pointer" : "move",
              borderRadius: "4px",
              border: "1px solid #1976d2",
              padding: isMobile ? "1px 4px" : "2px 6px",
            },
          })}
          dayPropGetter={() => ({
            style: { cursor: "pointer" },
            title: "Click to schedule an appointment",
          })}
          formats={{
            timeGutterFormat: "HH:mm",
            eventTimeRangeFormat: ({ start, end }) =>
              `${format(start, "HH:mm")} - ${format(end, "HH:mm")}`,
            agendaTimeRangeFormat: ({ start, end }) =>
              `${format(start, "HH:mm")} - ${format(end, "HH:mm")}`,
            dayRangeHeaderFormat: ({ start, end }) =>
              isMobile
                ? `${format(start, "dd/MM")} - ${format(end, "dd/MM")}`
                : `${format(start, "dd/MM/yyyy")} - ${format(end, "dd/MM/yyyy")}`,
            weekdayFormat: isMobile ? "eeeee" : "eee",
          }}
        />

        {loading && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              backgroundColor: "rgba(255, 255, 255, 0.6)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 10,
            }}
          >
            <Spin size="large" tip="Loading appointments..." />
          </div>
        )}
      </div>
      <Modal
        centered
        open={isModalOpen}
        onCancel={handleCancel}
        footer={null}
        width={isMobile ? "calc(100vw - 24px)" : "60vw"}
        confirmLoading={loading}
        styles={{
          body: {
            maxHeight: isMobile ? "75vh" : "80vh",
            overflowY: "auto",
            position: "relative",
            padding: isMobile ? "12px 12px 0" : undefined,
          },
        }}
        title={
          <div
            style={{
              textAlign: "center",
              fontWeight: 600,
              fontSize: isMobile ? "1.2rem" : "1.5rem",
              color: "#1f1f1f",
              borderBottom: "1px solid #f0f0f0",
              paddingBottom: 12,
            }}
          >
            {deleteMode ? (
              <>
                <DeleteOutlined style={{ color: "#ff4d4f", marginRight: 8 }} />
                <span>
                  Delete appointment for{" "}
                  <strong style={{ color: "#000" }}>
                    {selectedEvent?.patientName || "this patient"}
                  </strong>
                  ?
                </span>
              </>
            ) : isEdit ? (
              <>
                <EditOutlined style={{ color: "#1677ff", marginRight: 8 }} />
                <span>Edit Appointment</span>
              </>
            ) : (
              <>
                <CalendarOutlined style={{ color: "#52c41a", marginRight: 8 }} />
                Add Appointment
              </>
            )}
            <div
              style={{
                fontSize: isMobile ? 13 : 14,
                fontWeight: 400,
                color: "#6b7280",
                marginTop: 4,
              }}
            >
              {deleteMode ?
                ("Delete this appointment") :
                isEdit
                  ? "Update the appointment below"
                  : "Fill in the details to add a new appointment"}
            </div>
          </div>
        }
      >

        {deleteMode ? (
          <div>
            <p>This action cannot be undone.</p>
            <div
              style={{
                display: "flex",
                justifyContent: isMobile ? "stretch" : "flex-end",
                flexDirection: isMobile ? "column-reverse" : "row",
                gap: "0.5rem",
              }}
            >
              <Button block={isMobile} onClick={() => setDeleteMode(false)}>Cancel</Button>
              <Button block={isMobile} danger type="primary" onClick={handleConfirmDelete}>Yes, Delete</Button>
            </div>
          </div>
        ) : (
          <div
            ref={contentRef}
            style={{
              maxHeight: isMobile ? "55vh" : "65vh",
              overflowY: "auto",
              paddingRight: 8,
              WebkitOverflowScrolling: "touch",
            }}
          >
            <Form<FormValues>
              form={form}
              layout="vertical"
              onFinish={handleAddAndEditAppointment}
              initialValues={{ reminderTime: 15 }}
              style={{
                padding: `16px ${horizontalPadding}px 0`
              }}
            >
              {isEdit && <Form.Item name="id" label="Appointment ID"><Input disabled /></Form.Item>}
              <Form.Item
                name="startTime"
                label="Start Time"
                rules={[{ required: true, message: "Please select the start time!" }]}
              >
                <DatePicker showTime format="DD.MM.YYYY HH:mm" style={{ width: "100%" }} inputReadOnly={isMobile} />
              </Form.Item>
              <Form.Item
                name="endTime"
                label="End Time"
                dependencies={['startTime']}
                rules={[
                  { required: true, message: "Please select the end time!" },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('startTime') <= value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('End time must be after start time!'));
                    },
                  }),
                ]}
              >
                <DatePicker showTime format="DD.MM.YYYY HH:mm" style={{ width: "100%" }} inputReadOnly={isMobile} />
              </Form.Item>
              <Form.Item name="reason" label="Reason"><Input /></Form.Item>
              <Form.Item name="notes" label="Notes"><Input /></Form.Item>
              <Form.Item name="reminderTime" label="Reminder Time">
                <Select placeholder="Select reminder time">
                  <Select.Option value={null}>Don't remind me</Select.Option>
                  <Select.Option value={5}>5 minutes before</Select.Option>
                  <Select.Option value={10}>10 minutes before</Select.Option>
                  <Select.Option value={15} default>15 minutes before</Select.Option>
                  <Select.Option value={30}>30 minutes before</Select.Option>
                  <Select.Option value={60}>1 hour before</Select.Option>
                  <Select.Option value={120}>2 hours before</Select.Option>
                  <Select.Option value={1440}>1 day before</Select.Option>
                </Select>
              </Form.Item>
              <Form.Item name="patientId" label="Patient" rules={[{ required: true, message: "Please select a patient!" }]}>
                <Select showSearch onSearch={handleSearch} filterOption={false} disabled={isEdit} notFoundContent={fetching ? <Spin size="small" /> : "No results"} options={options} />
              </Form.Item>
            </Form>
          </div>
        )}
        {
          deleteMode ? <></> :
            <div
              style={{
                position: "sticky",
                bottom: 0,
                background: "#fff",
                padding: isMobile ? "12px 8px" : "16px 24px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 8,
                flexWrap: isMobile ? "wrap" : "nowrap",
                borderTop: "1px solid #f0f0f0",
                boxShadow: "0 -2px 8px rgba(0, 0, 0, 0.04)",
                zIndex: 10,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8, flex: isMobile ? 1 : undefined }}>
                <Button
                  onClick={handleCancel}
                  icon={<CloseOutlined />}
                  style={{ borderRadius: 6, flex: isMobile ? 1 : undefined }}
                >
                  Cancel
                </Button>

                {isEdit && (
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => setDeleteMode(true)}
                    style={{
                      borderRadius: 6,
                      backgroundColor: "#ff4d4f",
                      color: "#fff",
                      border: "none",
                      flex: isMobile ? 1 : undefined,
                    }}
                  >
                    Delete
                  </Button>
                )}
              </div>

              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={() => form.submit()}
                loading={loading}
                block={isMobile}
                style={{
                  borderRadius: 6,
                  padding: isMobile ? undefined : "0 24px",
                  fontWeight: 500,
                  flex: isMobile ? "1 0 100%" : undefined,
                }}
              >
                {isEdit ? "Update" : "Save"}
              </Button>
            </div>
        }
      </Modal>
      <Modal
        open={!!pendingDrag}
        title="Confirm Reschedule"
        centered
        width={isMobile ? "calc(100vw - 24px)" : 520}
        onCancel={() => setPendingDrag(null)}
        footer={[
          <Button key="cancel" block={isMobile} onClick={() => setPendingDrag(null)}>
            Cancel
          </Button>,
          <Button
            key="confirm"
            type="primary"
            block={isMobile}
            onClick={async () => {
              if (!pendingDrag) return;
              const { event, start, end } = pendingDrag;
              try {
                await BaseService.update(`/appointments/`, event.id, {
                  startTime: dayjs(start).format("DD.MM.YYYY HH:mm"),
                  endTime: dayjs(end).format("DD.MM.YYYY HH:mm"),
                });

                notify({
                  title: "Appointment Moved",
                  description: `${event.patientName}'s appointment was moved to ${dayjs(start).format("DD.MM.YYYY HH:mm")}.`,
                  variant: "success",
                });

                // Update UI locally
                setAppointments((prev) =>
                  prev.map((a) =>
                    a.id === event.id ? { ...a, start, end } : a
                  )
                );
              } catch (error) {
                notify({
                  title: "Error",
                  description: "Could not update appointment.",
                  variant: "error",
                });
              }
              setPendingDrag(null);
            }}
          >
            Confirm
          </Button>,
        ]}
      >
        <p>
          Move <strong>{pendingDrag?.event.patientName}</strong>’s appointment to{" "}
          <strong>{dayjs(pendingDrag?.start).format("DD.MM.YYYY HH:mm")}</strong>?
        </p>
      </Modal>

    </div>
  );
};

export default AppointmentCalendar;
